import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useState, useEffect } from 'react';
import { updateProducts, updateAlldata } from 'reducers/products';
import { shuffleArray } from 'utils/shuffleArray';
import Fuse from 'fuse.js';
import { useSearchParams } from 'react-router-dom';
import useLayout from './useLayout';

export default function useProducts() {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const { view } = useLayout();

  const auth = localStorage.getItem('auth') ?? '';
  const search = searchParams.get('search');

  const [products, setProducts] = useState([]);
  const [lookbook, setLookbook] = useState([]);
  const [allData, setAllData] = useState([]);

  const handleShuffleProducts = useCallback(
    (_data, view) => {
      const newData = view === 'TEXT' ? _data : shuffleArray(_data);
      dispatch(updateProducts(newData));
    },
    [dispatch],
  );

  const handleAllData = useCallback(
    _data => {
      dispatch(updateAlldata(_data));
    },
    [dispatch],
  );

  useEffect(() => {
    if (auth) {
      const fetchData = async () => {
        try {
          const productsResponse = await fetch(`https://d3mwemeqd71vo2.cloudfront.net/products.json?auth=${auth}`);
          const productsData = await productsResponse.json();
          setProducts(productsData);
          const lookbookResponse = await fetch(`https://d3mwemeqd71vo2.cloudfront.net/lookbook.json?auth=${auth}`);
          const lookbookData = await lookbookResponse.json();
          setLookbook(lookbookData);

          setAllData([...productsData, ...lookbookData]);
        } catch (error) {
          setProducts([]);
          setLookbook([]);
          setAllData([]);
        }
      };

      fetchData();
    }
  }, [auth]);

  useEffect(() => {
    if (allData.length > 0) {
      handleAllData(allData);
      handleShuffleProducts(allData, view);
    }
  }, [allData, view]);

  useEffect(() => {
    if (allData.length === 0) return;

    if (!search) {
      handleShuffleProducts(allData, view);
      return;
    }

    const detailsFuse = new Fuse(allData, {
      keys: ['number', 'code', 'name', 'category', 'size', 'origin'],
      threshold: 0.0,
      includeScore: true,
      includeMatches: true,
    });

    const overviewFuse = new Fuse(allData, {
      keys: ['colour', 'materials'],
      threshold: 0.3,
      distance: 50,
      includeScore: true,
      includeMatches: true,
    });

    const resultDetailProduct = detailsFuse.search(search);
    const resultOverviewProduct = overviewFuse.search(search);

    const combinedResults = [...resultDetailProduct, ...resultOverviewProduct];
    const searchData = Array.from(new Set(combinedResults.map(result => result.item)));

    handleShuffleProducts(searchData, view);
  }, [allData, search]);

  return {
    allData: useSelector(state => state.products.allData),
    products,
    lookbook,
    shuffledData: useSelector(state => state.products.shuffledProducts),
    updateProducts,
    handleShuffleProducts,
  };
}
